import { getRoleSwitchProgress } from '@/auth/roleSwitchProgress';
import { RETURN_URL_KEY, RETURN_URL_OWNER_KEY, ROLE_PAGE_PREFIX } from '@/auth/roleState';
import { getSessionUserId } from '@/auth/session';
import { activeAssignmentId } from '@/query/keys';

/**
 * Each role's last page (owner 2026-10-20): "each role should have the session URL where they
 * were present; with a common session URL it results in no-permission pages".
 *
 * While a role is active, every in-app navigation (auth/RolePageRecorder.tsx, mounted by
 * RoleGate) records the hash path (path + query, no host) under
 *
 *   localStorage `pnet.rolepage.<user id>.<assignment id>`
 *   localStorage `pnet.rolepage.<user id>.preview`   while a role preview runs
 *
 * so a role switch lands on the NEW role's own last page (else its home), switching back lands
 * where that role left off, and a preview never overwrites the user's own role's page. The
 * page to return to after a sign-in (`sessionurl`) carries its owner assignment
 * (`sessionurl_ra`); a deep link opened while signed out has none.
 *
 * Storage lifecycle (auth/roleState.ts): the per-role pages survive a switch and an expired
 * session and go on sign-out; the preview's page goes when the preview ends.
 *
 * The first landing of a role (after a switch, a sign-in, a preview start or end, or a page
 * load that adopted another role) is marked once (`markLanding`): a page gate that refuses
 * that page sends the user to the role's home instead of /nopermission (auth/PageGate.tsx
 * `RefusedRedirect`). Any later refusal is /nopermission as before.
 *
 * No ApiProvider import (ApiProvider imports this module).
 */

/** Hash routes that are never a page to return to. */
export const NOT_A_PAGE = new Set([
  '',
  'sso',
  'resetpassword',
  'login',
  'nopermission',
  'noaccess',
]);

/** The owner of the preview's page memory (one per user: a user previews one role at a time). */
export const PREVIEW_OWNER = 'preview';

export type RolePageOwner = number | typeof PREVIEW_OWNER;

export const rolePageKey = (userId: number, owner: RolePageOwner): string =>
  `${ROLE_PAGE_PREFIX}${userId}.${owner}`;

/**
 * The in-app page of an address or a hash path: `/path?query` (no host, no second hash), or
 * null for the login, SSO, reset-password, no-permission and no-access pages.
 */
export function pageOf(address: string | null | undefined): string | null {
  if (!address) return null;
  const text = String(address);
  const hashIndex = text.indexOf('#/');
  const hashPath = hashIndex >= 0 ? text.substring(hashIndex + 1) : text;
  if (!hashPath.startsWith('/')) return null;
  const [withQuery] = hashPath.split('#');
  const [path, query = ''] = withQuery.split('?');
  const name = path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (NOT_A_PAGE.has(name.toLowerCase()) || ['undefined', 'null'].includes(name)) return null;
  return `/${name}${query ? `?${query}` : ''}`;
}

/** The path part of a page (`/path?query` -> `/path`). */
export const pathOnly = (page: string): string => page.split('?')[0];

const local = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage blocked
  }
};

const session = (): Storage | null => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null; // storage blocked
  }
};

// ---- which memory is in use ---------------------------------------------------------------

let previewing = false;
let paused = false;

/** A role preview runs (auth/rolePreview.ts setCurrentPreview): pages go to the preview's memory. */
export const notePreviewRunning = (running: boolean): void => {
  previewing = running;
};

/**
 * Nothing is recorded from here until the page reloads (`true`), or again (`false`, the switch
 * failed): a role switch or preview start / end clears the session and loads the next role's
 * data under the page still on screen, whose gates may redirect meanwhile.
 */
export const pauseRolePages = (pause = true): void => {
  paused = pause;
};

/** The memory the current navigation belongs to: the preview's, else the active assignment's. */
export const currentRolePageOwner = (): RolePageOwner | null =>
  previewing ? PREVIEW_OWNER : activeAssignmentId();

// ---- the per-role pages ------------------------------------------------------------------

/**
 * Records `address` (a hash path or a full address) as the current role's last page, and as the
 * page the next sign-in returns to (not during a preview). False when nothing was recorded: no
 * user or role, a switch in progress, or a page that is never returned to.
 */
export function recordRolePage(address: string, href: string = window.location.href): boolean {
  if (paused || getRoleSwitchProgress() !== null) return false;
  const userId = getSessionUserId();
  const owner = currentRolePageOwner();
  const page = pageOf(address);
  if (userId === null || owner === null || page === null) return false;
  const storage = local();
  if (!storage) return false;
  try {
    storage.setItem(rolePageKey(userId, owner), page);
    if (owner !== PREVIEW_OWNER) {
      storage.setItem(RETURN_URL_KEY, href);
      storage.setItem(RETURN_URL_OWNER_KEY, String(owner));
    }
    return true;
  } catch {
    return false; // storage full
  }
}

/** The last page of `owner` (default: the current memory), or null. */
export function readRolePage(
  owner: RolePageOwner | null = currentRolePageOwner(),
  userId: number | null = getSessionUserId(),
): string | null {
  if (owner === null || userId === null) return null;
  try {
    return pageOf(local()?.getItem(rolePageKey(userId, owner)));
  } catch {
    return null;
  }
}

/** Forgets the preview's pages (the preview starts or ends), of every user of this browser. */
export function clearPreviewPages(): void {
  const storage = local();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(ROLE_PAGE_PREFIX) && key.endsWith(`.${PREVIEW_OWNER}`)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
}

/** True when a preview's pages are still in memory (a preview ran in this browser). */
export function hasPreviewPages(): boolean {
  const storage = local();
  if (!storage) return false;
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(ROLE_PAGE_PREFIX) && key.endsWith(`.${PREVIEW_OWNER}`)) return true;
    }
  } catch {
    /* storage unavailable */
  }
  return false;
}

// ---- the page to return to after a sign-in -------------------------------------------------

/** `sessionurl` routes that are not a page of their own (the home decides). */
const NOT_A_RETURN_PAGE = new Set(['dashboard']);

/**
 * The page `sessionurl` names when `assignmentId` may use it (path only, as before): a deep link
 * opened while signed out (no owner) is used by any role; a page noted by a signed-in role only
 * by that role.
 */
export function returnPageFor(assignmentId: number | null): string | null {
  try {
    const storage = local();
    const page = pageOf(storage?.getItem(RETURN_URL_KEY));
    if (page === null) return null;
    const owner = storage?.getItem(RETURN_URL_OWNER_KEY) ?? null;
    if (owner !== null && owner !== String(assignmentId)) return null;
    const path = pathOnly(page);
    return NOT_A_RETURN_PAGE.has(path.replace(/^\/+/, '')) ? null : path;
  } catch {
    return null;
  }
}

// ---- the one-shot landing marker -----------------------------------------------------------

/** sessionStorage (this tab only, survives the reload of a switch): the first landing's path. */
export const LANDING_KEY = 'pnet.landing';

/** Marks `page` as the first landing of a role (a gate refusing it goes home instead). */
export function markLanding(page: string): void {
  try {
    const path = pathOnly(page);
    if (path.startsWith('/')) session()?.setItem(LANDING_KEY, path);
  } catch {
    /* storage unavailable: a refusal shows /nopermission */
  }
}

const landingPath = (): string | null => {
  try {
    return session()?.getItem(LANDING_KEY) ?? null;
  } catch {
    return null;
  }
};

const dropLanding = (): void => {
  try {
    session()?.removeItem(LANDING_KEY);
  } catch {
    /* storage unavailable */
  }
};

/** True (once) when `pathname` is the marked first landing; the marker is consumed. */
export function takeLanding(pathname: string): boolean {
  if (landingPath() !== pathname) return false;
  dropLanding();
  return true;
}

/** A navigation to another page: the first landing is over. */
export function leaveLanding(pathname: string): void {
  const marked = landingPath();
  if (marked !== null && marked !== pathname) dropLanding();
}

/** Test helper. */
export const resetRolePagesForTests = (): void => {
  previewing = false;
  paused = false;
  dropLanding();
};
